import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, Phone } from 'lucide-react';
import Navbar from '../components/Navbar';
import { getDoctors, getMe, trackEvent } from '../services/api';

export default function AllDoctorsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getMe();
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    loadDoctors();
  }, []);

  const loadDoctors = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching doctors from API...');
      const response = await getDoctors({});
      console.log('Doctors response:', response.data);
      setDoctors(response.data || []);
    } catch (error) {
      console.error('Error loading doctors:', error);
      setError(error.message || 'Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const handleDoctorClick = async (doctorId) => {
    await trackEvent('profile_view', doctorId);
    navigate(`/doctor/${doctorId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={() => setUser(null)} />
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-4xl text-[#0F2A44] mb-2">
            All Doctors
          </h1>
          <p className="font-public text-[#4B5563]">
            Browse all {doctors.length} healthcare professionals on Afyagram
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-6 mb-6">
            <p className="font-public text-red-600">Error: {error}</p>
            <button 
              onClick={loadDoctors}
              className="mt-2 text-sm text-red-700 underline"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Doctors Grid */}
        {!error && doctors.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
            <p className="font-public text-[#4B5563] mb-4">No doctors registered yet.</p>
            <button 
              onClick={loadDoctors}
              className="text-sm text-[#4A90E2] underline"
            >
              Refresh
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {doctors.map((doctor) => (
              <div
                key={doctor.doctor_id}
                onClick={() => handleDoctorClick(doctor.doctor_id)}
                className="bg-white rounded-xl border border-slate-100 p-6 cursor-pointer hover-lift"
                data-testid={`doctor-card-${doctor.doctor_id}`}
              >
                {/* Doctor Image */}
                <div className="flex justify-center mb-4">
                  {doctor.photo_url ? (
                    <img
                      src={doctor.photo_url}
                      alt={doctor.name}
                      className="w-24 h-24 rounded-full object-cover border-2 border-slate-100"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center border-2 border-slate-100">
                      <span className="text-3xl text-slate-500 font-manrope font-bold">
                        {doctor.name.charAt(0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Doctor Info */}
                <div className="text-center mb-4">
                  <h3 className="font-manrope font-bold text-lg text-[#0F2A44] mb-1">
                    {doctor.title} {doctor.name}
                  </h3>
                  <p className="font-public text-sm text-[#4B5563] mb-2">
                    {doctor.specialty}
                  </p>

                  {/* Rating */}
                  <div className="flex items-center justify-center gap-1 mb-3">
                    <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" />
                    <span className="font-space font-medium text-[#262626]">
                      {doctor.average_rating.toFixed(1)}
                    </span>
                    <span className="text-xs text-[#4B5563] font-public">
                      ({doctor.total_reviews} reviews)
                    </span>
                  </div>

                  {/* Location */}
                  <div className="flex items-center justify-center gap-2 text-sm text-[#4B5563] mb-3">
                    <MapPin className="h-4 w-4" />
                    <span className="font-public">{doctor.region}</span>
                  </div>

                  {/* Badges */}
                  <div className="flex justify-center gap-2 mb-4">
                    {doctor.is_featured && (
                      <span className="px-2 py-1 bg-[#2DD4BF]/10 text-[#2DD4BF] text-xs font-medium rounded-full">
                        Featured
                      </span>
                    )}
                    {doctor.is_sponsored && (
                      <span className="px-2 py-1 bg-[#4A90E2]/10 text-[#4A90E2] text-xs font-medium rounded-full">
                        Sponsored
                      </span>
                    )}
                    {doctor.kmpdc_verified && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        ✓ KMPDC
                      </span>
                    )}
                  </div>
                </div>

                {/* Bio Preview */}
                {doctor.bio && (
                  <p className="font-public text-xs text-[#4B5563] text-center line-clamp-2 mb-4">
                    {doctor.bio}
                  </p>
                )}

                {/* Contact Info */}
                {doctor.contact_phone && (
                  <div className="flex items-center justify-center gap-2 text-xs text-[#4B5563] border-t border-slate-100 pt-4">
                    <Phone className="h-3 w-3" />
                    <span className="font-public">{doctor.contact_phone}</span>
                  </div>
                )}

                {/* Insurance Preview */}
                {doctor.insurances_accepted && doctor.insurances_accepted.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1 justify-center">
                    {doctor.insurances_accepted.slice(0, 3).map((insurance, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-slate-100 text-[#4B5563] text-xs rounded font-public">
                        {insurance}
                      </span>
                    ))}
                    {doctor.insurances_accepted.length > 3 && (
                      <span className="px-2 py-0.5 text-[#4B5563] text-xs font-public">
                        +{doctor.insurances_accepted.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
